/**
 * MINI_FIX_BS — per-PAJE story builder.
 *
 * Returns the STRUCTURED inputs needed to render the "Why was this PAJE
 * proposed?" sub-accordion in `PajesTab → PajeCard`. The helper is pure,
 * React-free, and format-free: i18n key + interpolation params + a
 * `direction` discriminator. The component layer formats currency
 * amounts and applies colours; this file decides ONLY the narrative.
 *
 * Critical: this module REUSES the regexes + `translateAccountKey`
 * already exported by `translatePajeDescription.ts`. We never write
 * brittle new parsers — if a pattern doesn't match, we degrade to the
 * generic story branch (still useful, never blank / crash).
 *
 * Inputs the story leans on:
 *   • `paje.type`        — FX_REVALUATION | ADJUSTMENT | RECLASSIFICATION
 *   • `paje.variance`    — signed string (negative → decrease, per MINI_FIX_BR)
 *   • `paje.description` — parsed by the existing regexes (FX | prior-recon)
 *   • `paje.suggestedJE` — Dr/Cr lines, fallback magnitude when variance is "0"
 *   • `paje.contractId`  — surfaced in every sentence
 */

import type { AggregatedPaje } from "@/types";

import {
  FX_REVALUATION_PATTERN,
  PRIOR_RECON_PATTERN,
  translateAccountKey,
  type TFn,
} from "./translatePajeDescription";

export interface PajeStory {
  /** i18n key for the "why generated" sentence. */
  whyKey: string;
  /** i18n interpolation params for whyKey. */
  whyParams: Record<string, unknown>;
  /** i18n key for the "what it means / what to do" sentence. */
  meaningKey: string;
  meaningParams: Record<string, unknown>;
  /** "increase" | "decrease" — drives the coloured direction line. */
  direction: "increase" | "decrease";
  /**
   * Raw absolute-magnitude string the component will currency-format
   * (e.g. `"66775.04"`). Exposed so the component can call
   * `formatCurrency(absVariance, currency)` without re-parsing.
   */
  absVariance: string;
}

export function buildPajeStory(paje: AggregatedPaje, t: TFn): PajeStory {
  const direction: PajeStory["direction"] = paje.variance.startsWith("-")
    ? "decrease"
    : "increase";
  // Strip the sign for sentence interpolation — the direction line
  // carries the sign meaning, so the amount reads positive in prose.
  const absVariance = paje.variance.startsWith("-")
    ? paje.variance.slice(1)
    : paje.variance;

  const common = { absVariance, direction } satisfies Pick<
    PajeStory,
    "absVariance" | "direction"
  >;

  if (paje.type === "FX_REVALUATION") {
    const fxMatch = paje.description.match(FX_REVALUATION_PATTERN);
    if (fxMatch) {
      const [, currency, closingRate, recordedRate] = fxMatch;
      return {
        ...common,
        whyKey: "paje.story.fx.why",
        whyParams: {
          contractId: paje.contractId,
          currency,
          closingRate,
          recordedRate,
        },
        meaningKey: "paje.story.fx.meaning",
        meaningParams: { direction },
      };
    }
    return {
      ...common,
      whyKey: "paje.story.fx.whyGeneric",
      whyParams: { contractId: paje.contractId },
      meaningKey: "paje.story.fx.meaning",
      meaningParams: { direction },
    };
  }

  if (paje.type === "ADJUSTMENT") {
    const priorMatch = paje.description.match(PRIOR_RECON_PATTERN);
    if (priorMatch) {
      const [, accountKey, toolVal, clientVal] = priorMatch;
      const account = translateAccountKey(accountKey!, t);
      return {
        ...common,
        whyKey: "paje.story.adjustment.why",
        whyParams: {
          contractId: paje.contractId,
          account,
          toolVal,
          clientVal,
        },
        meaningKey: "paje.story.adjustment.meaning",
        meaningParams: { account, direction },
      };
    }
    return {
      ...common,
      whyKey: "paje.story.adjustment.whyGeneric",
      whyParams: { contractId: paje.contractId },
      meaningKey: "paje.story.adjustment.meaningGeneric",
      meaningParams: { direction },
    };
  }

  // RECLASSIFICATION — the variance is the magnitude being shifted
  // between ST / LT buckets; total liability is unchanged.
  return {
    ...common,
    whyKey: "paje.story.reclassification.why",
    whyParams: { contractId: paje.contractId },
    meaningKey: "paje.story.reclassification.meaning",
    meaningParams: { direction },
  };
}
